// Phase 24a · Admin Payments dashboard
// Route: /admin/payments (admin only)
// Tabs: Overview / Payment Intents / Subscriptions / Refunds / Webhook Events
import React, { useEffect, useState, useCallback } from "react";
import {
    DollarSign, RefreshCcw, Users, Bell, FileText, Download,
    CheckCircle, XCircle, RotateCw, Filter, ChevronDown,
} from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL;

function eur(cents) {
    return `$${((cents || 0) / 100).toFixed(2)}`;
}
function fmt(dt) {
    if (!dt) return "";
    try { return new Date(dt).toLocaleString(); } catch { return dt; }
}

const TABS = [
    { id: "overview", label: "Overview", Icon: DollarSign },
    { id: "payment-intents", label: "Payment Intents", Icon: FileText },
    { id: "subscriptions", label: "Subscriptions", Icon: Users },
    { id: "refunds", label: "Refunds", Icon: RefreshCcw },
    { id: "webhook-events", label: "Webhooks", Icon: Bell },
];

export default function AdminPayments() {
    const [tab, setTab] = useState("overview");
    const [overview, setOverview] = useState(null);
    const [payments, setPayments] = useState([]);
    const [subs, setSubs] = useState([]);
    const [refunds, setRefunds] = useState([]);
    const [webhooks, setWebhooks] = useState([]);
    const [loading, setLoading] = useState(false);
    const [health, setHealth] = useState(null);
    const [refundFilter, setRefundFilter] = useState("pending");

    const load = useCallback(async () => {
        setLoading(true);
        try {
            if (tab === "overview") {
                const r = await fetch(`${API}/api/admin/payments/overview`, { credentials: "include" });
                if (r.ok) setOverview(await r.json());
                const h = await fetch(`${API}/api/admin/payments/health`, { credentials: "include" });
                if (h.ok) setHealth(await h.json());
            } else if (tab === "payment-intents") {
                const r = await fetch(`${API}/api/admin/payments/payment-intents?limit=100`, { credentials: "include" });
                if (r.ok) setPayments(((await r.json()) || {}).items || []);
            } else if (tab === "subscriptions") {
                const r = await fetch(`${API}/api/admin/payments/subscriptions?limit=100`, { credentials: "include" });
                if (r.ok) setSubs(((await r.json()) || {}).items || []);
            } else if (tab === "refunds") {
                const q = refundFilter ? `?status=${refundFilter}&limit=100` : "?limit=100";
                const r = await fetch(`${API}/api/admin/payments/refunds${q}`, { credentials: "include" });
                if (r.ok) setRefunds(((await r.json()) || {}).items || []);
            } else if (tab === "webhook-events") {
                const r = await fetch(`${API}/api/admin/payments/webhook-events?limit=100`, { credentials: "include" });
                if (r.ok) setWebhooks(((await r.json()) || {}).items || []);
            }
        } catch {}
        setLoading(false);
    }, [tab, refundFilter]);

    useEffect(() => {
        document.title = "Admin · Payments · Global Lead Suite";
        load();
    }, [load]);

    const approveRefund = async (id) => {
        const reason = window.prompt("Approve reason (optional):", "operator_approved");
        const r = await fetch(`${API}/api/admin/payments/refunds/${id}/approve`, {
            method: "POST", credentials: "include",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({ reason: reason || "" }),
        });
        if (r.ok) load();
    };

    const denyRefund = async (id) => {
        const reason = window.prompt("Deny reason:", "outside_policy");
        if (!reason) return;
        const r = await fetch(`${API}/api/admin/payments/refunds/${id}/deny`, {
            method: "POST", credentials: "include",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({ reason }),
        });
        if (r.ok) load();
    };

    const replayWebhook = async (eventId) => {
        const r = await fetch(`${API}/api/admin/payments/webhooks/replay/${eventId}`, {
            method: "POST", credentials: "include",
        });
        if (r.ok) load();
    };

    return (
        <div className="min-h-screen bg-[#0A0A0A] text-neutral-100">
            <div className="max-w-7xl mx-auto px-4 py-6">
                <div className="flex items-start justify-between mb-6">
                    <div>
                        <h1 className="text-2xl font-bold" data-testid="admin-payments-title">Payments</h1>
                        <p className="text-xs text-neutral-500 mt-0.5">
                            MonetizationEngine Foundation · Phase 24a
                            {health && (
                                <span className="ml-2 inline-flex items-center gap-1 px-1.5 py-0.5 rounded-full bg-[#1A1A1A] border border-[#1A1A1A]">
                                    <span className={`w-1.5 h-1.5 rounded-full ${health.payments_enabled ? "bg-[#CCCCCC]" : "bg-neutral-500"}`} />
                                    {health.payments_enabled ? "ENABLED" : "DISABLED"} · STRIPE_MODE={health.mode}
                                </span>
                            )}
                        </p>
                    </div>
                    <a
                        href={`${API}/api/admin/payments/audit-log/export.csv`}
                        data-testid="admin-payments-csv-export"
                        className="text-xs px-3 py-1.5 rounded-full border border-[#CCCCCC]/40 hover:bg-[#CCCCCC]/10 inline-flex items-center gap-1.5"
                    >
                        <Download size={12} /> Audit CSV
                    </a>
                </div>

                {/* Tabs */}
                <div className="flex flex-wrap gap-1 mb-6 border-b border-[#1A1A1A]" data-testid="admin-payments-tabs">
                    {TABS.map(({ id, label, Icon }) => (
                        <button
                            key={id}
                            onClick={() => setTab(id)}
                            data-testid={`admin-payments-tab-${id}`}
                            className={`text-xs px-3 py-2 inline-flex items-center gap-1.5 transition ${
                                tab === id
                                    ? "text-[#CCCCCC] border-b-2 border-[#CCCCCC]"
                                    : "text-neutral-500 hover:text-neutral-300"
                            }`}
                        >
                            <Icon size={12} /> {label}
                        </button>
                    ))}
                </div>

                {loading && <div className="text-sm text-neutral-500 mb-3">Loading…</div>}

                {/* Overview */}
                {tab === "overview" && overview && (
                    <div className="space-y-4" data-testid="admin-payments-overview">
                        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
                            <div className="rounded-xl border border-[#1A1A1A] bg-[#111111] p-4">
                                <p className="text-xs text-neutral-500">Total revenue</p>
                                <p className="text-2xl font-bold text-[#CCCCCC]" data-testid="kpi-total-revenue">
                                    {eur(overview.total_revenue_cents)}
                                </p>
                                <p className="text-[10px] text-neutral-500 mt-1">Sum of succeeded PI</p>
                            </div>
                            <div className="rounded-xl border border-[#1A1A1A] bg-[#111111] p-4">
                                <p className="text-xs text-neutral-500">MRR</p>
                                <p className="text-2xl font-bold text-[#CCCCCC]" data-testid="kpi-mrr">
                                    {eur(overview.mrr_cents)}
                                </p>
                                <p className="text-[10px] text-neutral-500 mt-1">Active subscriptions</p>
                            </div>
                            <div className="rounded-xl border border-[#1A1A1A] bg-[#111111] p-4">
                                <p className="text-xs text-neutral-500">Active subs</p>
                                <p className="text-2xl font-bold" data-testid="kpi-active-subs">
                                    {overview.active_subscriptions}
                                </p>
                                <p className="text-[10px] text-neutral-500 mt-1">
                                    {Object.entries(overview.subscriptions_by_status || {}).map(([k, v]) => `${k}:${v}`).join(" · ")}
                                </p>
                            </div>
                            <div className="rounded-xl border border-[#1A1A1A] bg-[#111111] p-4">
                                <p className="text-xs text-neutral-500">Pending refunds</p>
                                <p className="text-2xl font-bold text-neutral-200" data-testid="kpi-pending-refunds">
                                    {overview.pending_refunds}
                                </p>
                                <p className="text-[10px] text-neutral-500 mt-1">Awaiting operator</p>
                            </div>
                        </div>
                        {health && (
                            <div className="rounded-xl border border-[#1A1A1A] bg-[#111111] p-4" data-testid="admin-payments-ledger-counts">
                                <p className="text-xs text-neutral-500 uppercase tracking-widest mb-2">Ledger counts</p>
                                <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-7 gap-2 text-xs">
                                    {Object.entries(health.ledger_counts || {}).map(([k, v]) => (
                                        <div key={k} className="rounded-lg bg-[#0A0A0A] border border-[#1A1A1A] p-2">
                                            <p className="text-[10px] text-neutral-500 truncate">{k}</p>
                                            <p className="text-sm font-semibold">{v}</p>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        )}
                    </div>
                )}

                {/* Payment intents */}
                {tab === "payment-intents" && (
                    <div className="rounded-xl border border-[#1A1A1A] bg-[#111111] overflow-x-auto" data-testid="admin-payments-pi-table">
                        <table className="w-full text-xs">
                            <thead className="text-left text-neutral-500 border-b border-[#1A1A1A]">
                                <tr>
                                    <th className="px-3 py-2">stripe_id</th>
                                    <th className="px-3 py-2">user_id</th>
                                    <th className="px-3 py-2">amount</th>
                                    <th className="px-3 py-2">status</th>
                                    <th className="px-3 py-2">created</th>
                                </tr>
                            </thead>
                            <tbody>
                                {payments.map((p) => (
                                    <tr key={p.stripe_id} className="border-b border-[#1A1A1A]" data-testid={`pi-row-${p.stripe_id}`}>
                                        <td className="px-3 py-2 font-mono text-[11px] text-neutral-400 truncate max-w-[220px]">{p.stripe_id}</td>
                                        <td className="px-3 py-2 font-mono text-[11px] text-neutral-500 truncate max-w-[180px]">{p.user_id}</td>
                                        <td className="px-3 py-2">{eur(p.amount_cents)}</td>
                                        <td className="px-3 py-2 capitalize">
                                            <span className={`px-1.5 py-0.5 rounded-full text-[10px] ${
                                                p.status === "succeeded" ? "bg-[#CCCCCC]/15 text-[#CCCCCC]" :
                                                p.status === "failed" ? "bg-[#FAFAFA]/15 text-[#FAFAFA]" :
                                                p.status === "refunded" ? "bg-neutral-800 text-neutral-200" :
                                                "bg-neutral-500/15 text-neutral-400"
                                            }`}>{p.status}</span>
                                        </td>
                                        <td className="px-3 py-2 text-neutral-500">{fmt(p.created_at)}</td>
                                    </tr>
                                ))}
                                {payments.length === 0 && (
                                    <tr><td colSpan="5" className="px-3 py-6 text-center text-neutral-500">No payment intents</td></tr>
                                )}
                            </tbody>
                        </table>
                    </div>
                )}

                {/* Subscriptions */}
                {tab === "subscriptions" && (
                    <div className="rounded-xl border border-[#1A1A1A] bg-[#111111] overflow-x-auto" data-testid="admin-payments-subs-table">
                        <table className="w-full text-xs">
                            <thead className="text-left text-neutral-500 border-b border-[#1A1A1A]">
                                <tr>
                                    <th className="px-3 py-2">stripe_id</th>
                                    <th className="px-3 py-2">product</th>
                                    <th className="px-3 py-2">status</th>
                                    <th className="px-3 py-2">operator</th>
                                    <th className="px-3 py-2">created</th>
                                </tr>
                            </thead>
                            <tbody>
                                {subs.map((s) => (
                                    <tr key={s.stripe_id} className="border-b border-[#1A1A1A]" data-testid={`sub-row-${s.stripe_id}`}>
                                        <td className="px-3 py-2 font-mono text-[11px] text-neutral-400 truncate max-w-[220px]">{s.stripe_id}</td>
                                        <td className="px-3 py-2">{s.product_slug || "-"}</td>
                                        <td className="px-3 py-2 capitalize">{s.status}</td>
                                        <td className="px-3 py-2">
                                            {s.pending_operator_approval ? (
                                                <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-neutral-800 text-neutral-200">pending</span>
                                            ) : s.operator_approved ? (
                                                <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-[#CCCCCC]/15 text-[#CCCCCC]">approved</span>
                                            ) : (
                                                <span className="text-[10px] text-neutral-500">—</span>
                                            )}
                                        </td>
                                        <td className="px-3 py-2 text-neutral-500">{fmt(s.created_at)}</td>
                                    </tr>
                                ))}
                                {subs.length === 0 && (
                                    <tr><td colSpan="5" className="px-3 py-6 text-center text-neutral-500">No subscriptions</td></tr>
                                )}
                            </tbody>
                        </table>
                    </div>
                )}

                {/* Refunds */}
                {tab === "refunds" && (
                    <div className="space-y-3" data-testid="admin-payments-refunds">
                        <div className="flex items-center gap-2 text-xs">
                            <Filter size={12} className="text-neutral-500" />
                            <select
                                value={refundFilter}
                                onChange={(e) => setRefundFilter(e.target.value)}
                                data-testid="admin-refunds-filter"
                                className="bg-[#111111] border border-[#1A1A1A] rounded-full px-3 py-1"
                            >
                                <option value="">All</option>
                                <option value="pending">Pending</option>
                                <option value="succeeded">Succeeded</option>
                                <option value="denied">Denied</option>
                                <option value="failed">Failed</option>
                            </select>
                        </div>
                        <div className="rounded-xl border border-[#1A1A1A] bg-[#111111] overflow-x-auto">
                            <table className="w-full text-xs">
                                <thead className="text-left text-neutral-500 border-b border-[#1A1A1A]">
                                    <tr>
                                        <th className="px-3 py-2">id</th>
                                        <th className="px-3 py-2">PI</th>
                                        <th className="px-3 py-2">amount</th>
                                        <th className="px-3 py-2">reason</th>
                                        <th className="px-3 py-2">status</th>
                                        <th className="px-3 py-2">actions</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {refunds.map((r) => (
                                        <tr key={r.id} className="border-b border-[#1A1A1A]" data-testid={`refund-row-${r.id}`}>
                                            <td className="px-3 py-2 font-mono text-[10px] text-neutral-500 truncate max-w-[140px]">{r.id}</td>
                                            <td className="px-3 py-2 font-mono text-[10px] text-neutral-500 truncate max-w-[160px]">{r.payment_intent_id}</td>
                                            <td className="px-3 py-2">{eur(r.amount_cents)}</td>
                                            <td className="px-3 py-2 text-neutral-400 truncate max-w-[180px]">{r.reason || "—"}</td>
                                            <td className="px-3 py-2 capitalize">{r.status}</td>
                                            <td className="px-3 py-2">
                                                {r.status === "pending" ? (
                                                    <div className="flex gap-1">
                                                        <button
                                                            onClick={() => approveRefund(r.id)}
                                                            data-testid={`refund-approve-${r.id}`}
                                                            className="inline-flex items-center gap-1 text-[10px] px-2 py-1 rounded-full bg-[#CCCCCC]/15 text-[#CCCCCC] hover:bg-[#CCCCCC]/25"
                                                        >
                                                            <CheckCircle size={10} /> Approve
                                                        </button>
                                                        <button
                                                            onClick={() => denyRefund(r.id)}
                                                            data-testid={`refund-deny-${r.id}`}
                                                            className="inline-flex items-center gap-1 text-[10px] px-2 py-1 rounded-full bg-[#FAFAFA]/15 text-[#FAFAFA] hover:bg-[#FAFAFA]/25"
                                                        >
                                                            <XCircle size={10} /> Deny
                                                        </button>
                                                    </div>
                                                ) : (
                                                    <span className="text-[10px] text-neutral-500">—</span>
                                                )}
                                            </td>
                                        </tr>
                                    ))}
                                    {refunds.length === 0 && (
                                        <tr><td colSpan="6" className="px-3 py-6 text-center text-neutral-500">No refunds</td></tr>
                                    )}
                                </tbody>
                            </table>
                        </div>
                    </div>
                )}

                {/* Webhook events */}
                {tab === "webhook-events" && (
                    <div className="rounded-xl border border-[#1A1A1A] bg-[#111111] overflow-x-auto" data-testid="admin-payments-webhooks-table">
                        <table className="w-full text-xs">
                            <thead className="text-left text-neutral-500 border-b border-[#1A1A1A]">
                                <tr>
                                    <th className="px-3 py-2">event_id</th>
                                    <th className="px-3 py-2">type</th>
                                    <th className="px-3 py-2">verified</th>
                                    <th className="px-3 py-2">processed</th>
                                    <th className="px-3 py-2">retries</th>
                                    <th className="px-3 py-2">created</th>
                                    <th className="px-3 py-2">actions</th>
                                </tr>
                            </thead>
                            <tbody>
                                {webhooks.map((w) => (
                                    <tr key={w.stripe_event_id} className="border-b border-[#1A1A1A]" data-testid={`webhook-row-${w.stripe_event_id}`}>
                                        <td className="px-3 py-2 font-mono text-[10px] text-neutral-500 truncate max-w-[180px]">{w.stripe_event_id}</td>
                                        <td className="px-3 py-2 text-neutral-300">{w.type}</td>
                                        <td className="px-3 py-2">{w.signature_verified ? "✓" : <span className="text-neutral-200">unverified</span>}</td>
                                        <td className="px-3 py-2">{w.processed ? <span className="text-[#CCCCCC]">yes</span> : <span className="text-neutral-200">no</span>}</td>
                                        <td className="px-3 py-2">{w.retry_count || 0}</td>
                                        <td className="px-3 py-2 text-neutral-500">{fmt(w.created_at)}</td>
                                        <td className="px-3 py-2">
                                            <button
                                                onClick={() => replayWebhook(w.stripe_event_id)}
                                                data-testid={`webhook-replay-${w.stripe_event_id}`}
                                                className="inline-flex items-center gap-1 text-[10px] px-2 py-1 rounded-full border border-[#1A1A1A] hover:border-[#CCCCCC]/40"
                                            >
                                                <RotateCw size={10} /> Replay
                                            </button>
                                        </td>
                                    </tr>
                                ))}
                                {webhooks.length === 0 && (
                                    <tr><td colSpan="7" className="px-3 py-6 text-center text-neutral-500">No webhook events</td></tr>
                                )}
                            </tbody>
                        </table>
                    </div>
                )}
            </div>
        </div>
    );
}
